import { MatchUpStatusEnum } from '../_shared/models/csTypes/enums/matchup_status';
import {
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  SimpleChanges,
  ViewChild,
  inject,
} from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import {
  IonContent,
  IonRefresher,
  IonSegment,
  MenuController,
  NavController,
} from '@ionic/angular';
import { Socket } from 'ngx-socket-io';
import { delay, finalize, takeUntil } from 'rxjs/operators';

import { ScoreboardComponent } from '../scoreboard/scoreboard';
import { ShareMatchComponent } from '../_shared/components/share-match/share-match.component';

import { MatchService } from '../_core/services/match.service';
import { AuthService } from '../auth/auth.service';
import { LoaderService } from '../_core/services/loader.service';

import { Match } from '../_shared/models/match.model';

import { Capacitor } from '@capacitor/core';
import { Subject, Subscription } from 'rxjs';
import { MediaService } from '../_core/services/media.service';
import { TieService } from '../_core/services/tie.service';
import { KeepAwake } from '@capacitor-community/keep-awake';
import { StartMatchService } from './start-match/start-match.service';
import { StatsPage } from '../stats/stats.page';
import { Location } from '@angular/common';
import { SocketService } from '../_core/services/socket.service';
import { Score } from '../_shared/models/csTypes/score';
import { Set } from '../_shared/models/set.model';
import { Preferences } from '@capacitor/preferences';
import { AppStateService } from '../_core/app-state.service';

@Component({
  standalone: false,
  selector: 'app-match-tracker',
  templateUrl: './match-tracker.page.html',
  styleUrls: ['./match-tracker.page.scss'],
})
export class MatchTrackerPage implements OnInit, OnDestroy {
  public authService = inject(AuthService);
  public appStateService = inject(AppStateService);

  @ViewChild('scoreboard') scoreboard: ScoreboardComponent;
  @ViewChild('share') shareMatch: ShareMatchComponent;
  @ViewChild('segment') segment: IonSegment;
  @ViewChild('content') content: IonContent;
  @ViewChild('stats') statsPage: StatsPage;
  @ViewChild('segmentToolbar') segmentToolbar: ElementRef;

  matchId: string;
  match: Match;
  segmentValue: string;

  isOwner = false;
  isTracker = false;
  matchActive = false;
  isStreamView = false;

  private readonly destroy$: Subject<void> = new Subject();
  locationSubscription = new Subscription();
  refreshMatchTimeout: any;
  deviceId: string;
  broadcastDelay: number = 0;
  isVideoStreaming: boolean = false;

  constructor(
    public matchService: MatchService,
    private activatedRoute: ActivatedRoute,
    private socket: Socket,
    private loaderService: LoaderService,
    private mediaService: MediaService,
    private startMatchService: StartMatchService,
    private menuController: MenuController,
    private tieService: TieService,
    private navController: NavController,
    private location: Location,
    private socketService: SocketService
  ) {
    this.broadcastDelay =
      Number(this.activatedRoute.snapshot.queryParams.broadcastDelay) || 0;
  }

  async ngOnInit() {
    this.isStreamView =
      this.location.path().includes('stream-view') ||
      this.location.path().includes('playsight') ||
      this.location.path().includes('canada') ||
      this.location.path().includes('time-tennis');
    this.deviceId = (await Preferences.get({ key: 'deviceId' }))?.value;

    

    this.getDeviceInfo();
    this.subscriptions();
    this.appStateListener();
  }

  async ngOnDestroy() {
    if (
      Capacitor.isNativePlatform() &&
      Capacitor.isPluginAvailable('KeepAwake')
    ) {
      await KeepAwake.allowSleep();
    }

    this.socketService.leaveRoomAndRemoveSocketListener(
      `match/${this.match._id}/score`
    );
    this.socketService.leaveRoomAndRemoveSocketListener(
      `match/${this.match._id}/sets`
    );
    this.socketService.leaveRoomAndRemoveSocketListener(
      `match/${this.match._id}/edit`
    );
    if (this.match?.tie) {
      this.socketService.leaveRoomAndRemoveSocketListener(this.match?.tie);
    }

    this.destroy$.next();
    this.destroy$.complete();
    this.locationSubscription.unsubscribe();
  }

  ionViewDidEnter() {
    this.matchId = this.activatedRoute.snapshot.params.id;
    this.getMatch();
  }

  doRefresh(refresher: IonRefresher) {
    this.getMatch(false, refresher);
  }

  private async getDeviceInfo() {
    if (
      Capacitor.isNativePlatform() &&
      Capacitor.isPluginAvailable('KeepAwake')
    ) {
      await KeepAwake?.keepAwake();
    }
  }

  async getMatch(isUrlChanged = false, refresher?) {
    if (!refresher && !this.isStreamView) {
      await this.loaderService.present();
    }

    this.matchService
      .show(this.matchId)
      .pipe(
        takeUntil(this.destroy$),
        finalize(async () => {
          await this.loaderService.dismiss();
          if (refresher) refresher.detail.complete();
        })
      )
      .subscribe({
        next: async (res) => {
          this.match = res.data;
          this.isOwner =
            this.match.dist.owner === this.authService.authUserSignal()?._id;
          this.isTracker = this.matchService.isTracker(this.match);
          this.segmentValue = this.isTracker ? 'tracker' : 'points';

          if (isUrlChanged) {
            this.mediaService.publishMediaUpdate(
              this.matchId,
              this.match.dist.media
            );
          }
          if (this.match.sets?.length) {
            this.matchActive = true;
          }
         // Check if the match has cameras OR videoLinks with valid stream
         this.isVideoStreaming =
         this.match?.cameras?.some(camera =>
           camera?.extensions?.some(extension => extension?.value?.stream)
         ) ||
         (this.match?.videoLinks &&
           this.match.videoLinks.extensions?.some(extension => extension?.value?.stream)) || false;
       await this.menuController.close('tracker');
        

          await this.menuController.close('tracker');

          // Call to refresh a match for live stream in case it gets stuck
          this.setRefreshMatchTimeout();
          this.socketSubscriptions();
        },
      });
  }

  updateMatch(ev?) {
    // Listening to the event from tracker board
    if (ev?.match) this.match = Object.assign({}, ev.match);
    if (ev?.hasOwnProperty('isTracker')) {
      // Somebody else takes over tracking and the user subscribes to live follow
      this.isTracker = ev.isTracker;
      if (!this.isTracker) {
        this.socketSubscriptions();
      }
    }
  }

  private async socketSubscriptions() {
    this.socket.emit('join-room', `match/${this.match._id}/score`);
    this.socket.emit('join-room', `match/${this.match._id}/sets`);
    this.socket.emit('join-room', `match/${this.match._id}/edit`);
    if (this.match?.tie) {
      this.socket.emit('join-room', this.match.tie);
    }

    this.socketService
      .getSocketUpdate('match/score')
      .pipe(takeUntil(this.destroy$), delay(this.broadcastDelay * 1000))
      .subscribe(
        (data: {
          _id: string;
          score: Score;
          matchStatus: string;
          winningSide?: number;
          deviceId?: string;
        }) => {
          if (
            data &&
            data._id === this.match._id &&
            (!this.deviceId ||
              !data.deviceId ||
              data.deviceId !== this.deviceId)
          ) {
            this.match.score = data.score;
            this.match.matchStatus = data.matchStatus;
            this.match.winningSide = data.winningSide;
          }

          if (this.refreshMatchTimeout) {
            clearTimeout(this.refreshMatchTimeout);
            this.setRefreshMatchTimeout();
          }
        }
      );

    this.socketService
      .getSocketUpdate('match/sets')
      .pipe(takeUntil(this.destroy$), delay(this.broadcastDelay * 1000))
      .subscribe((data: { _id: string; sets: Set[], deviceId?: string }) => {
        if (data && data._id === this.match._id && (!this.deviceId || !data.deviceId || data.deviceId !== this.deviceId)) {
          this.match.sets = data.sets;
        }
      });

    this.socketService
      .getSocketUpdate('match/edit')
      .pipe(takeUntil(this.destroy$), delay(this.broadcastDelay * 1000))
      .subscribe((data: { matchUp: Match, deviceId?: string }) => {
        if (data && data.matchUp._id === this.match._id && (!this.deviceId || !data.deviceId || data.deviceId !== this.deviceId)) {          
          this.match = data.matchUp;
        }
      });
  }

  isAuthenticated() {
    this.getMatch();
  }

  getBackRoute() {
  const match = this.match;
  const tabParam = this.activatedRoute.snapshot.queryParams.tab;

  // --------------------------
  // Case 1: Tournament Route
  // --------------------------
  if (match?.tournament) {

    const validTabs = ['live-score', 'upcoming', 'completed', 'draw'];

    // If a valid tab query param exists, use it
    if (validTabs.includes(tabParam)) {
      return `tournaments/${match.tournament}/${tabParam}`;
    }

    // Map match status to tab
    const statusToTabMap: Record<string, string> = {
      [MatchUpStatusEnum.COMPLETED]: 'completed',
      [MatchUpStatusEnum.RETIRED]: 'completed',
      [MatchUpStatusEnum.DEFAULTED]: 'completed',
      [MatchUpStatusEnum.IN_PROGRESS]: 'live-score',
      [MatchUpStatusEnum.SUSPENDED]: 'live-score',
      TBP: 'live-score'
    };

    const fallbackTab = statusToTabMap[match.matchStatus] || 'upcoming';

    return `tournaments/${match.tournament}/${fallbackTab}`;
  }

  // --------------------------
  // Case 2: Tie Route
  // --------------------------
  if (match?.tie) {
    return `ties/${match.tie}`;
  }

  // --------------------------
  // Case 3: Individual Route
  // --------------------------
  return 'individual';
}

  updateStats(filters) {
    this.statsPage.updateStats(filters);
  }

  subscriptions() {
    this.tieService
      .subscribeMatchChange()
      .pipe(takeUntil(this.destroy$))
      .subscribe((matchId) => {
        this.matchActive = false;
        this.matchId = matchId;
        this.getMatch(true);
      });

    this.startMatchService
      .subscribeStartMatch()
      .pipe(takeUntil(this.destroy$))
      .subscribe((res) => {
        if (res.matchId === this.match._id) {
          this.matchActive = res.matchActive;
        }
      });

    this.matchService
      .subscribeDelete()
      .pipe(takeUntil(this.destroy$))
      .subscribe((deletedMatch) => {
        if (deletedMatch._id === this.match._id) {
          this.navController.back();
        }
      });
  }

  private setRefreshMatchTimeout() {
    if (
      this.isStreamView &&
      (this.match.matchStatus === MatchUpStatusEnum.IN_PROGRESS ||
        this.match.matchStatus === 'TBP')
    ) {
      this.refreshMatchTimeout = setTimeout(() => {
        this.getMatch();
      }, 120000);
    }
  }

  private async appStateListener() {
    this.appStateService.appState$
      .pipe(takeUntil(this.destroy$))
      .subscribe((isActive) => {
        if (
          isActive &&
          (this.match?.matchStatus === 'TBP' ||
            this.match?.matchStatus === MatchUpStatusEnum.IN_PROGRESS ||
            this.match?.matchStatus === MatchUpStatusEnum.SUSPENDED)
        ) {
          this.socket.emit('reconnect', `match/${this.match._id}/score`);
          this.socket.emit('reconnect', `match/${this.match._id}/sets`);
          this.socket.emit('reconnect', `match/${this.match._id}/edit`);
        }
      });
  }
}
